import type { Software, Category, SoftwareVersion, SoftwareComment } from "@/types";
import { softwareList } from "@/mocks/software";
import { categories } from "@/mocks/categories";
import { commentPool } from "@/mocks/comments";

export function getSoftwareById(id: string): Software | undefined {
  return softwareList.find((item) => item.id === id);
}

export function getSoftwareByCategory(categoryId: string): Software[] {
  return softwareList.filter((item) => item.categoryId === categoryId);
}

export function getCategoryById(id: string): Category | undefined {
  return categories.find((item) => item.id === id);
}

export function searchSoftware(keyword: string): Software[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return softwareList.filter((item) => {
    return (
      item.name.toLowerCase().includes(kw) ||
      item.shortDesc.toLowerCase().includes(kw) ||
      item.developer.toLowerCase().includes(kw) ||
      item.tags.some((tag) => tag.toLowerCase().includes(kw))
    );
  });
}

export function getRelatedSoftware(software: Software, count = 4): Software[] {
  const sameCategory = softwareList.filter(
    (item) => item.categoryId === software.categoryId && item.id !== software.id,
  );
  const tagMatched = softwareList.filter(
    (item) =>
      item.id !== software.id &&
      item.categoryId !== software.categoryId &&
      item.tags.some((tag) => software.tags.includes(tag)),
  );
  return [...sameCategory, ...tagMatched].slice(0, count);
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

export function buildVersionHistory(software: Software): SoftwareVersion[] {
  const parts = software.version.split(".").map((p) => Number(p) || 0);
  const notesList = [
    "优化启动速度，修复若干已知问题，提升整体稳定性。",
    "新增功能入口与个性化设置，改善部分机型兼容性。",
    "更新界面细节与交互体验，修复反馈的闪退问题。",
    "上线全新内容与活动，优化网络连接与加载速度。",
  ];
  const base = new Date(software.updatedAt);
  const result: SoftwareVersion[] = [];
  for (let i = 0; i < 4; i += 1) {
    const nextParts = [...parts];
    if (i > 0) {
      nextParts[nextParts.length - 1] = Math.max(0, nextParts[nextParts.length - 1] - i);
    }
    const d = new Date(base);
    d.setDate(base.getDate() - i * 27);
    result.push({
      version: nextParts.join("."),
      date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
      notes: notesList[i] || notesList[notesList.length - 1],
    });
  }
  return result;
}

export function pickComments(softwareId: string, count = 4): SoftwareComment[] {
  const seed = softwareId.split("").reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  const start = seed % commentPool.length;
  const result: SoftwareComment[] = [];
  for (let i = 0; i < count; i += 1) {
    result.push(commentPool[(start + i) % commentPool.length]);
  }
  return result;
}

export function formatDownloads(value: number): string {
  if (value >= 100000000) return `${(value / 100000000).toFixed(1)}亿`;
  if (value >= 10000) return `${(value / 10000).toFixed(0)}万`;
  return `${value}`;
}

export function platformLabel(platform: string): string {
  return platform === "ios" ? "iOS" : "安卓";
}

export function parseSizeToMB(size: string): number {
  const value = parseFloat(size);
  if (Number.isNaN(value)) return 0;
  if (/gb/i.test(size)) return value * 1024;
  return value;
}